/**
 * useRecaptcha — Feb 2026. Wraps Google reCAPTCHA v3 for React.
 *
 * v3 is invisible — no puzzle, no widget. You call `execute(action)` right
 * before submitting a form; you get back a token; you send the token with
 * your request; the backend calls Google's siteverify with the SECRET key
 * to score it.
 *
 * Docs: https://developers.google.com/recaptcha/docs/v3
 *
 * Usage:
 *   const recaptcha = useRecaptcha();
 *   const token = await recaptcha.execute("register");
 *   await api.post("/auth/register", body, {
 *     headers: { "X-Recaptcha-Token": token },
 *   });
 *
 * Configure REACT_APP_RECAPTCHA_SITE_KEY in the frontend .env. If missing,
 * execute() returns "" and the backend fails open (dev-friendly).
 */
import { useEffect, useMemo, useState } from "react";

const SCRIPT_ID  = "recaptcha-v3-script";
const SITE_KEY   = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let _loadPromise = null;

function loadRecaptcha() {
    if (!SITE_KEY) return Promise.resolve(false);
    if (_loadPromise) return _loadPromise;
    _loadPromise = new Promise((resolve) => {
        if (window.grecaptcha) return resolve(true);
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) { existing.addEventListener("load", () => resolve(true)); return; }
        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
        s.async = true;
        s.defer = true;
        s.onload  = () => resolve(true);
        s.onerror = () => resolve(false);
        document.head.appendChild(s);
    });
    return _loadPromise;
}

export function useRecaptcha() {
    const [ready, setReady] = useState(!!window.grecaptcha);

    useEffect(() => {
        let cancel = false;
        loadRecaptcha().then((ok) => { if (!cancel) setReady(ok); });
        return () => { cancel = true; };
    }, []);

    const api = useMemo(() => ({
        ready,
        enabled: !!SITE_KEY,
        async execute(action) {
            // Site-key missing → dev/local mode → fail open. Backend also
            // fails open when its SECRET key is missing, so this stays safe.
            if (!SITE_KEY) return "";
            const ok = await loadRecaptcha();
            if (!ok || !window.grecaptcha?.execute) return "";
            return new Promise((resolve) => {
                window.grecaptcha.ready(async () => {
                    try {
                        const token = await window.grecaptcha.execute(SITE_KEY, { action });
                        resolve(token || "");
                    } catch {
                        resolve("");
                    }
                });
            });
        },
    }), [ready]);

    return api;
}
